import React, { useState, useEffect } from 'react';

import { Box, SimpleGrid, Button, Icon, Text, VStack, Progress } from '@chakra-ui/react';

import { FaCog, FaRuler, FaPlay, FaPause, FaUsers } from 'react-icons/fa';

import { useColors } from './colors';
import socket from '../../../app/socket';

const ActionButtons = ({
  onScheduleSettings,
  onRulesSettings,
  onStartProcess,
  onStopProcess,
  onCompetitorSettings,
  isProcessRunning = false,
}) => {
  const { backgroundCardColor, textColor, borderColor } = useColors();
  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    percentage: 0,
    status: 'idle',
    message: '',
  });

  useEffect(() => {
    // Connect to socket
    socket.connect();

    // Listen for progress updates
    socket.on('repricer-progress', (data) => {
      setProgress(data);
    });

    // Listen for status updates
    socket.on('repricer-status', (data) => {
      if (data.isRunning) {
        setProgress((prev) => ({
          ...prev,
          status: 'running',
          message: data.message,
        }));
      } else {
        setProgress((prev) => ({
          ...prev,
          status: 'idle',
          message: data.message,
        }));
      }
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, []);

  const actionButtons = [
    {
      label: 'Schedule Settings',
      description: 'Configure optimization schedule',
      icon: FaCog,
      onClick: onScheduleSettings,
      colorScheme: 'blue',
      variant: 'outline',
    },
    {
      label: 'Pricing Rules',
      description: 'Set competitive pricing rules',
      icon: FaRuler,
      onClick: onRulesSettings,
      colorScheme: 'purple',
      variant: 'outline',
    },
    {
      label: 'Competitor Settings',
      description: 'Manage competitor settings',
      icon: FaUsers,
      onClick: onCompetitorSettings,
      colorScheme: 'purple',
      variant: 'outline',
    },
    {
      label: progress.status === 'running' ? 'Stop Process' : 'Start Process',
      description:
        progress.status === 'running'
          ? 'Stop optimization process'
          : 'Start optimization process',
      icon: progress.status === 'running' ? FaPause : FaPlay,
      onClick: progress.status === 'running' ? onStopProcess : onStartProcess,
      colorScheme: progress.status === 'running' ? 'red' : 'green',
      variant: 'solid',
    },
  ];

  return (
    <Box
      p={6}
      bg={backgroundCardColor}
      borderRadius="lg"
      boxShadow="sm"
      borderWidth="1px"
      borderColor={borderColor}
    >
      <Text color={textColor} fontSize="lg" fontWeight="semibold" mb={4}>
        Optimization Controls
      </Text>
      <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
        {actionButtons.map((button, index) => (
          <Button
            key={index}
            onClick={button.onClick}
            colorScheme={button.colorScheme}
            variant={button.variant}
            size="lg"
            height="auto"
            py={6}
            px={4}
            isDisabled={button.isDisabled}
            _hover={
              !button.isDisabled
                ? {
                    transform: 'translateY(-2px)',
                    boxShadow: 'md',
                  }
                : {}
            }
            transition="all 0.2s"
          >
            <VStack spacing={2} align="center">
              <Icon as={button.icon} boxSize={6} />
              <Box textAlign="center">
                <Text fontSize="sm" fontWeight="semibold">
                  {button.label}
                </Text>
                <Text fontSize="xs" opacity={0.8}>
                  {button.description}
                </Text>
                {button.label === 'Start Process' && progress.status === 'running' && (
                  <VStack spacing={1} mt={2}>
                    <Progress
                      value={progress.percentage}
                      size="sm"
                      colorScheme="green"
                      width="100%"
                      borderRadius="full"
                    />
                    <Text fontSize="xs" color="gray.600">
                      {progress.current}/{progress.total} ({progress.percentage}%)
                    </Text>
                    {progress.message && (
                      <Text fontSize="xs" color="gray.500" textAlign="center">
                        {progress.message}
                      </Text>
                    )}
                  </VStack>
                )}
              </Box>
            </VStack>
          </Button>
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default ActionButtons;
